<template>
  <v-container fluid class="tb-layout-browse1 rv-page">
    <!-- Retrieve previous date excel dialog -->
    <v-dialog
      ref="dialog"
      v-model="prevRecordDialog"
      :return-value.sync="prevRecordDate"
      max-width="340"
      content-class="rv-dialog--datepicker"
    >
      <div class="rv-datepicker-card">
        <div class="rv-datepicker-card__header">
          <v-icon size="20" color="#fff" class="mr-2">mdi-calendar-search</v-icon>
          <span class="rv-datepicker-card__title">Select Date</span>
          <v-spacer></v-spacer>
          <button class="rv-datepicker-card__close" @click="triggerPrevRecord()">
            <v-icon size="18" color="rgba(255,255,255,0.7)">mdi-close</v-icon>
          </button>
        </div>
        <v-date-picker
          v-model="prevRecordDate"
          scrollable
          color="var(--app-accent)"
          full-width
          no-title
          class="rv-datepicker-card__picker"
        ></v-date-picker>
        <div class="rv-datepicker-card__footer">
          <button class="rv-datepicker-card__btn rv-datepicker-card__btn--cancel" @click="triggerPrevRecord()">Cancel</button>
          <button class="rv-datepicker-card__btn rv-datepicker-card__btn--confirm" @click="retrievePrevRecord(prevRecordDate)">View Records</button>
        </div>
      </div>
    </v-dialog>

    <!-- Dialog for Generating PV Status -->
    <v-dialog max-width="700" v-model="generatePvDialog" persistent content-class="rv-dialog">
      <v-card class="rv-dialog__card">
        <div class="rv-dialog__header">
          <v-icon class="rv-dialog__header-icon" color="white" size="22">mdi-progress-clock</v-icon>
          <span class="rv-dialog__header-title">Generating PV Status</span>
        </div>
        <div class="rv-dialog__body">
          <v-simple-table dense class="rv-table bordered-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Application</th>
                  <th class="text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in generatingPvApplications"
                  :key="index"
                >
                  <td>{{ item.application }}</td>
                  <td class="d-flex justify-center align-center">
                    <v-progress-circular
                      v-if="item.status == PAYMENT_VOUCHER_EXECUTING_STATUS"
                      indeterminate
                      size="20"
                      color="amber"
                    ></v-progress-circular>
                    <v-icon
                      v-else
                      :color="generatingPvStatusIcon(item.status).color"
                      >{{ generatingPvStatusIcon(item.status).icon }}</v-icon
                    >
                  </td>
                </tr>
                <tr v-show="!generatingPvApplications">
                  <td colspan="2" class="text-center">
                    Preparing data
                    <v-progress-circular
                      indeterminate
                      size="20"
                      color="amber"
                    ></v-progress-circular>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
        <div class="rv-dialog__actions">
          <button
            class="rv-dialog__btn rv-dialog__btn--confirm"
            @click="toggleGeneratePvDialog(false)"
            :disabled="isGeneratePvDialogDisabled"
          >
            Close
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog for Retrieve Previous Record -->
    <v-dialog max-width="1000" v-model="recordsDialog" content-class="rv-dialog">
      <v-card class="rv-dialog__card">
        <div class="rv-dialog__header">
          <v-icon class="rv-dialog__header-icon" color="white" size="22">mdi-history</v-icon>
          <span class="rv-dialog__header-title">Previous Record</span>
        </div>
        <div class="rv-dialog__body">
          <div class="rv-dialog__record-meta">
            <span class="rv-dialog__record-date">Date: {{ prevRecordTitleDate }}</span>
            <a href="#" class="rv-dialog__download-link" @click="jsonToExcel(retrievedExcelDatas)">
              <v-icon size="16" color="#56bde8" class="mr-1">mdi-download</v-icon>
              Download Excel
            </a>
          </div>
          <v-simple-table dense class="rv-table bordered-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Beneficial Acc Name</th>
                  <th class="text-left">Price (RM)</th>
                  <th class="text-left">Reference</th>
                  <th class="text-left">Bank</th>
                  <th class="text-left" width="40">Payment Voucher</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, key) in retrievedDatas" :key="key">
                  <td>{{ item.Payee }}</td>
                  <td>{{ item.Paid }}</td>
                  <td>{{ item.Reference }}</td>
                  <td>{{ item["Bank Type"] }}</td>
                  <td class="text-center">
                    <v-icon v-if="!item.pvFileUrl" disabled
                      >mdi-file-eye-outline</v-icon
                    >
                    <v-icon
                      v-else
                      :disabled="!item.pvFileUrl"
                      color="red lighten-2"
                      v-for="(data, key) in item.pvFileUrl"
                      :key="key"
                      @click="previewPv(data.fileUrl)"
                      >mdi-file-eye-outline</v-icon
                    >
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
        <div class="rv-dialog__actions">
          <button
            class="rv-dialog__btn rv-dialog__btn--cancel"
            @click="toggleRecordsDialog(false)"
          >
            Close
          </button>
        </div>
      </v-card>
    </v-dialog>

    <v-card class="pa-2 rv-card-wrapper">
      <AAlertBanner :messages="alertError" hide-title icon="mdi-alert-circle-outline" />

      <!-- Hidden filter components (kept for refs used by methods) -->
      <div style="display: none;">
        <BreadHeader :title="headerTitle"></BreadHeader>
        <BreadTabulatorSelectColumn
          ref="selectColumn"
          :model="model"
          @applyColumnSelection="applyColumnSelection"
        ></BreadTabulatorSelectColumn>
        <BreadTabulatorFilterable
          ref="filter"
          :model="model"
          :setConditions="setConditions"
        ></BreadTabulatorFilterable>
      </div>

      <!-- Page header -->
      <div class="page-toolbar">
        <div class="page-toolbar__left">
          <h1 class="page-toolbar__title">{{ headerTitle }}</h1>
          <span id="record-count" class="page-toolbar__count"></span>
        </div>
        <div class="page-toolbar__right" v-if="isFinanceApAdminRoles && !btnDisabled">
          <ApApplicationSelection
            :apApplicationSelectionDialog="apApplicationSelectionDialog"
            :apiUrl="this.apiGetBrowseUrl()"
            @showRefundClientForm="showRefundClientForm"
            @toggleSelectionDialog="toggleSelectionDialog"
            @downloadExcel="downloadExcelData"
            @prevRecord="triggerPrevRecord"
          ></ApApplicationSelection>
        </div>
      </div>

      <!-- Search bar -->
      <div class="page-search">
        <BreadTabulatorSearchable
          ref="search"
          :model="model"
          :setConditions="setConditions"
        ></BreadTabulatorSearchable>
      </div>

      <div class="table-scroll-wrapper">
        <BreadDataTable
          ref="tabulatorDataTable"
          layout="fitColumns"
          :autoColumns="false"
          :paginationSize="paginationSize"
          :paginationSizeSelector="paginationSizeSelector"
          :apiUrl="apiGetBrowseUrl()"
          :columns="columns"
          :modelKey="modelKey"
          :deferInitialLoad="true"
          :columnSelectionDisabledCount="columnSelectionDisabledCount"
          @getCurrentPageSize="getCurrentPageSize"
          @getCurrentSort="getCurrentSort"
          @getCurrentColumnWidth="getCurrentColumnWidth"
          @getCurrentPageNumber="getCurrentPageNumber"
        ></BreadDataTable>
      </div>
    </v-card>
    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
    <ApRejectButton
      :rejectDialog="rejectDialog"
      :errorField="errorField"
      :formData="formData"
      @closeRejectDialog="closeRejectDialog()"
      @rejectApplicationToAR="rejectApplicationToAR"
    ></ApRejectButton>
  </v-container>
</template>
<script>
import Fuse from "fuse.js";
import JSZip from "jszip";
import axios from "axios";
import * as XLSX from "xlsx";
import Api from "@/objects/api";
import { mapState } from "vuex";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import HrdcData from "@/services/hrdc/objects/globalData";
import ApRejectButton from "@/services/hrdc/views/finance_ap_submit_refund_to_cimb/ApRejectButton";
import ApApplicationSelection from "@/services/hrdc/views/finance_ap_submit_refund_to_cimb/ApApplicationSelection";

export default {
  mixins: [hrdcFunction],
  components: {
    ApApplicationSelection,
    ApRejectButton,
  },
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
      hrdc: (state) => state.hrdc.data,
    }),
    headerTitle() {
      return `${process.env.VUE_APP_SERVICE_HRDC_TAXPOD_APP_NAME} ${this.model.name.plural}`;
    },
  },
  props: ["params"],
  data: () => ({
    api: new Api(),
    apiSubmit: new Api(),
    apiRejectApplicationToAR: new Api(),
    apiRetrievePrevApRecord: new Api(),
    apiScanPdfText: new Api(),
    apiDownloadOrPreviewAttachment: new Api(),
    conditions: [],
    model: new Model(),
    service: new Service(),
    hrdcData: new HrdcData(),
    listAllViews: [],
    btnDisabled: false,
    columns: [],
    paginationSize: 10,
    paginationSizeSelector: [10, 50, 100, 200, 500],
    modelKey: "",
    cColumnSort: [],
    displayRowCount: 10,
    selectedCustomColums: [],
    selectedFilterConditions: [],
    switchToAdvanceFilter: false,
    isShow: false,
    allResizeColumns: [],
    columnSelectionDisabledCount: 3,
    currentPageNo: 1,
    errorField: {
      rejectReason: null,
    },
    formData: {
      applicationUuid: null,
      rejectReason: null,
    },
    alertError: [],
    loadingDialog: false,
    rejectDialog: false,
    apApplicationSelectionDialog: false,
    prevRecordDialog: false,
    prevRecordDate: new Date(
      Date.now() - new Date().getTimezoneOffset() * 60000
    )
      .toISOString()
      .substr(0, 10),
    generatePvDialog: false,
    generatingPvApplications: null,
    isGeneratePvDialogDisabled: true,
    recordsDialog: false,
    retrievedDatas: [],
    retrievedExcelDatas: [],
    prevRecordTitleDate: null,
  }),
  async created() {
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);
    this.modelKey = this.params.modelKey;
    this.columns = this.$_.filter(
      this.model.browse.table.headers,
      (headers) => {
        return headers;
      }
    );

    this.serviceDataAssignation(this.model.serviceKey);

    const apiDetails = {
      api: {
        url: `${this.$service.hrdc}/v1/en/console/crmView/list?modelKey=${this.model.key}`,
      },
      apiSubmit: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/finance_refund/create-finance-refund-bulk`,
        method: "post",
        headers: {
          Authorization: `Bearer ${this.$store.state.auth.data.token}`,
        },
      },
      apiRejectApplicationToAR: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/finance_refund/reject-application-to-ar`,
        method: "post",
      },
      apiRetrievePrevApRecord: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/finance_refund/retrieve-previous-ap-record`,
      },
      apiScanPdfText: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/finance_refund/scan-pdf`,
        method: "post",
        headers: {
          Authorization: `Bearer ${this.$store.state.auth.data.token}`,
        },
      },
      apiDownloadOrPreviewAttachment: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/azure-download-or-preview-application-attachment`,
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
      if (apiDetails[api].headers !== undefined) {
        this[api].setHeaders(apiDetails[api].headers);
      }
    }

    var columnIndex = 0;
    for (var column of this.columns) {
      // Add click company cell direct to view details.
      if (column.field == "bankAccountHolderName") {
        this.columns[columnIndex].cellClick = (_, cell) => {
          const {
            applicationUuid,
            stageId,
            firstNameAccEnrollment,
            lastNameAccEnrollment,
            contactNoAccEnrollment,
            emailAccEnrollment,
          } = cell.getData();

          this.$store.commit("assignHrdcData", {
            applicationUuid,
            stageId,
            firstNameAccEnrollment,
            lastNameAccEnrollment,
            contactNoAccEnrollment,
            emailAccEnrollment,
            viewId: this.$route.query.viewId,
          });

          this.$router.push({
            name: "ViewHrdcApplication",
            query: {
              currentPageNo: this.currentPageNo,
              _ausk: applicationUuid,
            },
            params: {
              backTo: "RefundEmailApToCimb",
            },
          });
        };
      }

      columnIndex++;
    }

    this.columns.push({
      title: "Action",
      field: "action",
      headerSort: false,
      hozAlign: "center",
      headerHozAlign: "center",
      width: "190",
      formatter: () => {
        let btn = `
          <button type="button" class="view-pdf tb-action-btn tb-action-btn--blue">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/><path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/></svg>
            View PDF
          </button>
        `;

        // Only finance-ap roles can reject application to AR stages.
        if (this.isFinanceApAdminRoles) {
          btn += `
            <button type="button" class="reject-btn tb-action-btn tb-action-btn--red">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 18 18 6M6 6l12 12"/></svg>
              Reject
            </button>
          `;
        }

        return btn;
      },
      /**
       * Tabulator handed the formatter an `onRendered` hook to bind listeners
       * onto the cell's real DOM node; the v-data-table renderer has no such
       * hook (formatters run before any DOM exists). One delegated cellClick on
       * the cell replaces both per-row listeners — and it no longer re-binds on
       * every re-render the way the old code did.
       */
      cellClick: (e, cell) => {
        const btn = e.target.closest("button");
        if (!btn) return;

        e.stopPropagation(); // Prevent row click event

        if (btn.classList.contains("view-pdf")) {
          this.previewCompiledPdf(cell.getData().applicationUuid);
        } else if (btn.classList.contains("reject-btn")) {
          this.rejectDialog = true;
          this.formData.applicationUuid = cell.getData().applicationUuid;
        }
      },
      htmlOutput: true,
    });

    this.rolesAssignation(this.auth.roles);

    this.stages = await this.hrdcData.allStages;

    this.banks = this.model.banks;

    this.rolesAssignation(this.auth.roles);

    if (!this.isFinanceApAdminRoles) {
      this.alertError.push("You are not allowed to perform this action!");
      this.btnDisabled = true;
    }
  },
  mounted() {
    const viewId = this.$route.query.viewId;

    if (viewId !== "all" && viewId !== "trashed") {
      this.getAllViews(parseInt(viewId, 10));
    } else {
      this.getAllViews(viewId);
    }
  },
  methods: {
    /** Fetches the compiled AR-to-AP attachment for an application and opens it. */
    async previewCompiledPdf(applicationUuid) {
      const previewAtchUrl = `${process.env.VUE_APP_SERVICE_URL_HRDC}/v1/en/console/application/get-attachment-via-stage`;
      const streamUrl = `${process.env.VUE_APP_SERVICE_URL_HRDC}/v1/en/console/application/azure-download-or-preview-application-attachment`;

      this.showLoadingDialog();

      try {
        const response = await axios.post(
          previewAtchUrl,
          {
            applicationNo: applicationUuid,
            stageId: this.hrdcData.stagePriority.refundEmailArToAp,
          },
          {
            headers: {
              Authorization: `Bearer ${this.$store.state.auth.data.token}`,
            },
          }
        );

        if (!response.status) {
          throw new Error(response.data);
        }

        const { data } = response.data;
        const compiledPdfObj = data[0];

        const stream = await axios.get(streamUrl, {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            filePath: compiledPdfObj.fileUrl,
          },
        });

        const blobUrl = URL.createObjectURL(
          new Blob([stream.data], { type: "application/pdf" })
        );

        window.open(blobUrl, "_blank");
      } catch (err) {
        console.log(err);
        this.$store.dispatch("showMessage", {
          message:
            "Failed to Preview/Download attachment, kindly contact administrator",
          messageType: "error",
          timeout: 2000,
        });
      } finally {
        this.hideLoadingDialog();
      }
    },
    getConditions() {
      return this.conditions;
    },
    setConditions(conditions) {
      this.selectedFilterConditions = conditions;
      this.conditions = conditions;
      this.$refs.tabulatorDataTable.getFilterCondition(conditions);
    },
    apiGetBrowseUrl() {
      // Check if user have salesperson role, then assign only this salesperson can see back own sales.
      let browseUrl = `${this.$service[this.service.key]}/v1/en/console/${
        this.model.key
      }`;

      if (this.isSalespersonRoles && this.auth.roles.length == 1) {
        browseUrl = browseUrl + `?email=${this.auth.email}`;
      }

      return browseUrl;
    },
    apiGetBrowseTrashUrl() {
      let browseDeleteUrl = `
          ${this.$service[this.service.key]}/v1/en/console/${
        this.model.key
      }?deleted=true
        `;

      if (this.isSalespersonRoles && this.auth.roles.length == 1) {
        browseDeleteUrl = browseDeleteUrl + `&email=${this.auth.email}`;
      }

      return browseDeleteUrl;
    },
    setFiltersViaConditions(conditions) {
      this.$refs.filter.setFiltersViaConditions(conditions);
    },
    getCurrentPageSize(pagesize) {
      this.displayRowCount = pagesize;
    },
    getCurrentSort(sorters) {
      this.cColumnSort = sorters;
      this.cColumnSort = sorters.map((sorter) => {
        return {
          column: sorter.field,
          dir: sorter.dir,
        };
      });
    },
    getCurrentColumnWidth(resizedColumns) {
      const resizedColumnField = resizedColumns.getField();
      const resizedColumnWidth = resizedColumns.getWidth();

      //set the resized column width and filed name to the array
      const resizedColumnWidthAndField = {
        field: resizedColumnField,
        width: resizedColumnWidth,
      };

      const index = this.allResizeColumns.findIndex((item) => {
        return item.field === resizedColumnField;
      });

      if (index !== -1) {
        this.allResizeColumns[index].width = resizedColumnWidth;
      } else {
        this.allResizeColumns.push(resizedColumnWidthAndField);
      }
    },
    /**
     * This page renders no custom-view tab strip (no BreadTabulatorViewLatest),
     * so `$refs.customView` is undefined here. Calling it threw part-way through
     * changeTableDetails, which silently skipped setCurrentPageTo() — and with
     * the table's initial load now deferred it would leave it stuck loading.
     */
    selectViewTab(viewId, tabIndex) {
      if (this.$refs.customView) {
        this.$refs.customView.changeSelectedTabStatus(viewId, tabIndex);
      }
    },
    changeTableDetails(listAllViews, viewId) {
      // get the view details from listAllViews where id is equal to viewId
      if (viewId == "trashed") {
        this.$refs.tabulatorDataTable.callApiUrl(this.apiGetBrowseTrashUrl());
        this.$refs.selectColumn.showAllColumns([]);
        this.$refs.tabulatorDataTable.callSavedHeaderWidth([]);
        this.$refs.search.getFilterCondition([]); /// add this one
        this.selectViewTab("trashed", 0);
      } else if (viewId == "all") {
        this.$refs.tabulatorDataTable.callSavedHeaderWidth([
          {
            field: "rowSelection",
            width: 50,
          },
          {
            field: "index",
            width: 70,
          },
          {
            field: "price",
            width: 150,
          },
          {
            field: "action",
            width: 200,
          },
        ]);
        this.$refs.selectColumn.showAllColumns([]);
        this.$refs.search.getFilterCondition([]);
        this.selectViewTab("all", 1);
        this.setCurrentPageTo();
      } else {
        const tabIndex =
          listAllViews.indexOf(listAllViews.find((x) => x.id === viewId)) + 2;
        this.selectViewTab(viewId, tabIndex);
        const singleViewDetail = listAllViews.filter((item) => {
          return item.id === viewId;
        });

        if (singleViewDetail.length > 0) {
          if (
            JSON.parse(singleViewDetail[0].manageColumnCustomWidth).length > 0
          ) {
            this.$refs.tabulatorDataTable.callSavedHeaderWidth(
              JSON.parse(singleViewDetail[0].manageColumnCustomWidth)
            );
          }

          this.$refs.selectColumn.showViewSelectedColumns(
            JSON.parse(singleViewDetail[0].cColumnCondition)
          );

          // calling API
          if (JSON.parse(singleViewDetail[0].filterCondition).length > 0) {
            this.$refs.filter.setFiltersViaConditions(
              JSON.parse(singleViewDetail[0].filterCondition)
            );
          }

          // calling API
          if (JSON.parse(singleViewDetail[0].cColumnSort).length > 0) {
            this.$refs.tabulatorDataTable.callSavedColumnSort(
              JSON.parse(singleViewDetail[0].cColumnSort)
            );
          }

          // calling API
          if (singleViewDetail[0].displayRowCount != this.paginationSize) {
            this.$refs.tabulatorDataTable.callSavedPageSize(
              singleViewDetail[0].displayRowCount
            );
          }
          // add following one
          this.$refs.search.getFilterCondition(
            JSON.parse(singleViewDetail[0].filterCondition)
          );
        }

        this.setCurrentPageTo();
      }
    },
    getAllViews(viewId) {
      this.api.setCallbackCompleted((response) => {
        // get all private views when the ouwnerUuid is equal to auth.uuid
        const privateViews = response.filter((view) => {
          return view.isPrivate === true && view.ownerUuid === this.auth.uuid;
        });

        // get all public views
        const publicViews = response.filter((view) => {
          return view.isPrivate === false;
        });

        // push all private and public views to listAllViews
        this.listAllViews = [...publicViews, ...privateViews];
        this.changeTableDetails(this.listAllViews, viewId);
      });
      // Safety net: the table's initial auto-load is deferred (deferInitialLoad)
      // and is only released once a view is applied above. If the view list
      // fails to load, release it explicitly so the table isn't stuck loading.
      this.api.setCallbackError(() => {
        if (this.$refs.tabulatorDataTable) {
          this.$refs.tabulatorDataTable.releaseDeferredLoad();
        }
      });
      this.api.fetch();
    },
    applyColumnSelection(selectedColumns) {
      this.selectedCustomColums = selectedColumns;
      this.$refs.tabulatorDataTable.applyColumnSelection(selectedColumns);
    },
    getCurrentPageNumber(pageno) {
      this.currentPageNo = pageno;
    },
    setCurrentPageTo() {
      var pageNo =
        this.$store.state.developer.data.paginationPageNo != null
          ? this.$store.state.developer.data.paginationPageNo
          : 1;
      this.$refs.tabulatorDataTable.setCurrentPagNumber(pageNo);
      // Last step of applying a view. Restoring page 1 is a no-op for the
      // table, so release the deferred initial load explicitly rather than
      // relying on one of the earlier calls having changed request state.
      // Both paths coalesce into the same single request.
      this.$refs.tabulatorDataTable.releaseDeferredLoad();
      this.$store.commit("assignDeveloperData", {
        paginationPageNo: null,
      });
    },
    clearErrorMsg() {
      for (let error in this.errorField) {
        this.errorField[error] = null;
      }
    },
    showErrorMessage(response) {
      for (const key in response.data) {
        this.errorField[key] = null;
        this.errorField[key] = response.data[key];
      }
    },
    showRefundClientForm(apSelectedDate) {
      let allRecords = this.$refs.tabulatorDataTable.selected;

      // Check if any row is selected or not
      if (allRecords.length == 0) {
        this.$store.dispatch("showMessage", {
          message: "Please select at least one row!",
          messageType: "error",
          timeout: 2000,
        });
        return;
      }

      // Check only allow 10 attachments run concurrently.
      if (allRecords.length > 10) {
        this.$store.dispatch("showMessage", {
          message: "Please select at most 10 applications at one time.",
          messageType: "error",
          timeout: 2000,
        });
        return;
      }

      let selectedRecords = allRecords.map((row) => {
        const {
          applicationUuid,
          creditNoteNo,
          price,
          cnDate,
          bankAccountHolderName: oriCompanyName,
          compiledPdfFileUrl,
          cnFileUrl,
        } = row;
        return {
          applicationUuid,
          creditNoteNo,
          reference: `${row.creditNoteNo}/${row.autoGeneratedTaxpodInvoiceNo}`,
          price,
          cnDate,
          companyName: oriCompanyName,
          compiledPdfFileUrl,
          cnFileUrl,
        };
      });

      if (selectedRecords) {
        this.submit(selectedRecords, apSelectedDate);
      }
    },
    async downloadExcelData() {
      let allRecords = this.$refs.tabulatorDataTable.selected;

      if (allRecords.length == 0) {
        this.$store.dispatch("showMessage", {
          message: "Please select at least one row!",
          messageType: "error",
          timeout: 2000,
        });
        return;
      }

      this.jsonToExcel(this.getSelectedRowToExcel());
      this.$refs.tabulatorDataTable.clearSelection();
    },
    triggerPrevRecord() {
      this.prevRecordDialog = !this.prevRecordDialog;
    },
    retrievePrevRecord(date) {
      this.showLoadingDialog();

      this.prevRecordTitleDate = date;

      this.apiRetrievePrevApRecord.setQuery(
        `date=${date}&stageId=${this.hrdcData.stagePriority.apSubmitRefundToCimb}&pvStageId=${this.hrdcData.stagePriority.paymentVoucher}`
      );
      this.apiRetrievePrevApRecord.setCallbackCompleted((response) => {
        try {
          const { status, data, message } = response;

          if (!status) {
            this.$store.dispatch("showMessage", {
              message,
              messageType: "error",
              timeout: 2000,
            });

            return false;
          }

          if (status) {
            // To empty previous record.
            this.retrievedDatas = [];
            this.retrievedExcelDatas = [];

            for (const item of data) {
              const datas = {
                Requestor: this.learnTaxPodEmail,
                Reference:
                  item.creditNoteNo + "/" + item.autoGeneratedTaxpodInvoiceNo,
                Payee: this.getPayeeName(item.bankAccountHolderName),
                Paid: this.getPrice(item.price),
                "Bank Type": this.getBankName(item.bankName),
                "Account No.": this.getAccountNo(item.bankAccountNo),
              };
              this.retrievedExcelDatas.push(datas);

              const datasWithPv = { ...datas, pvFileUrl: item.fileUrl };
              this.retrievedDatas.push(datasWithPv);
            }

            this.triggerPrevRecord();
            this.toggleRecordsDialog(true);
          }
        } catch (err) {
          console.log(err.message);
          this.$store.dispatch("showMessage", {
            message: "Error, please contact administrator for support!",
            messageType: "error",
            timeout: 2000,
          });
        } finally {
          this.hideLoadingDialog();
        }
      });
      this.apiRetrievePrevApRecord.fetch();
    },
    jsonToExcel(data) {
      try {
        // Convert JSON data to a worksheet
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();

        for (let i = 2; i <= data.length + 1; i++) {
          ws[`D${i}`].z = '_(* #,##0.00_);_(* (#,##0.00);_(* "-"??_);_(@_)';
        }

        XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

        // Trigger the download
        XLSX.writeFile(wb, `HRDC ${this.name1} Refund Details.xlsx`);
      } catch (err) {
        console.log(err);
      }
    },
    findBankType(bankName) {
      const options = {
        includeScore: true,
        shouldSort: true,
        isCaseSensitive: false,
        threshold: 0.3,
        keys: ["name", "bankType", "abbreviation"],
      };

      // Check whether able to direct get bank from bank lists or not.
      const getBankFromBankLists = this.banks.find((item) => {
        const splitName = item.bankType.split("-");
        return (
          splitName[1] &&
          splitName[1].trim().toLowerCase() === bankName.toLowerCase()
        );
      });

      if (getBankFromBankLists) {
        return getBankFromBankLists.bankType;
      }

      let searchTextFormat = bankName
        .replace(/\b(?:bank|berhad|bhd|MALAYSIA|islamic|(M))\b/gi, "")
        .trim();

      const fuse = new Fuse(this.banks, options);
      const result = fuse.search(searchTextFormat);

      if (result.length == 0) {
        return bankName;
      } else {
        let bankType = result[0].item.bankType
          ? result[0].item.bankType
          : bankName;
        return bankType;
      }
    },
    getSelectedRowToExcel() {
      return this.$refs.tabulatorDataTable.selected.map((row) => {
        const {
          autoGeneratedTaxpodInvoiceNo,
          creditNoteNo,
          price,
          bankAccountHolderName,
          bankName,
          bankAccountNo,
        } = row;

        return {
          Requestor: this.learnTaxPodEmail,
          Reference: creditNoteNo + "/" + autoGeneratedTaxpodInvoiceNo,
          Payee: this.getPayeeName(bankAccountHolderName),
          Paid: this.getPrice(price),
          "Bank Type": this.getBankName(bankName),
          "Account No.": this.getAccountNo(bankAccountNo),
        };
      });
    },
    getPayeeName(bankAccountHolderName) {
      return bankAccountHolderName.toUpperCase().replace(/\u00A0/g, " ");
    },
    getAccountNo(bankAccNo) {
      return bankAccNo.replace(/[-\s]/g, "");
    },
    getBankName(bankName) {
      return this.findBankType(bankName);
    },
    getPrice(price) {
      return parseFloat(price.toFixed(2).toLocaleString());
    },
    async submit(selectedRecords, cimbSubmitedDate) {
      this.toggleGeneratePvDialog(true);
      this.toggleSelectionDialog(false);

      const zip = new JSZip();
      const companyCountMap = {};

      const extraApplications = [];
      for (const application of selectedRecords) {
        // Check whether is this application consist of >1 CN.
        const [cnPart, invPart] = application.reference.split("/");
        const invArr = invPart.split(",");

        // Split the CN claim amount.
        this.apiScanPdfText.setParams({
          fileUrl: application.cnFileUrl,
        });

        await this.$axios({
          method: this.apiScanPdfText.method,
          url: this.apiScanPdfText.url,
          data: this.apiScanPdfText.params,
          headers: this.apiScanPdfText.headers,
        }).then((res) => {
          const response = res.data;
          if (!response.status) {
            this.$store.dispatch("showMessage", {
              message: response.message,
              messageType: "error",
              timeout: 2000,
            });
            return false;
          }

          if (response.status) {
            for (const [index, cnNo] of cnPart.split(",").entries()) {
              const cnObj = response.data.find((item) => item.cnNo == cnNo);
              if (cnObj) {
                extraApplications.push({
                  applicationUuid: application.applicationUuid,
                  creditNoteNo: cnNo,
                  reference: `${cnNo}/${invArr[index]}`,
                  price: cnObj.price,
                  cnDate: application.cnDate,
                  companyName: application.companyName,
                  compiledPdfFileUrl: application.compiledPdfFileUrl,
                });
              }
            }
          }
        });
      }

      const updatedRecords = [...extraApplications];

      for (const record of updatedRecords) {
        // Check for duplicate company that claims > 1 set.
        if (companyCountMap[record.companyName]) {
          companyCountMap[record.companyName] += 1; // Increment count for future duplicates
          record.companyName = `${record.companyName} - ${
            companyCountMap[record.companyName]
          }`;
        } else {
          companyCountMap[record.companyName] = 1;
        }
      }

      this.generatingPvApplications = updatedRecords.map((item) => {
        return {
          applicationUuid: item.applicationUuid,
          application: `YYCGST- ${item.companyName.toUpperCase()}`,
          status: this.PAYMENT_VOUCHER_PENDING_STATUS,
          creditNoteNo: item.creditNoteNo,
        };
      });

      let data = {
        financeRefundToClientDate: cimbSubmitedDate,
        userUuid: this.auth.uuid,
        username: this.auth.name,
        editMode: this.editMode,
        // Xero client secret + token URL are applied server-side now; the
        // client only sends the grant type.
        grantType: "refresh_token",
        currentDate: this.localeDate().format("YYYY-MM-DD HH:mm:ss"),
      };

      for (const record of updatedRecords) {
        // Update application status that is currently running.
        let beneficialName = `YYCGST- ${record.companyName.toUpperCase()}`;
        const index = this.generatingPvApplications.findIndex(
          (item) =>
            item.applicationUuid === record.applicationUuid &&
            item.creditNoteNo === record.creditNoteNo
        );

        this.generatingPvApplications[index].status =
          this.PAYMENT_VOUCHER_EXECUTING_STATUS;

        data.selectedApplications = [record];

        await this.$axios({
          method: this.apiSubmit.method,
          url: this.apiSubmit.url,
          data,
          headers: this.apiSubmit.headers,
          responseType: "arraybuffer",
        })
          .then((response) => {
            const decoder = new TextDecoder("utf-8");
            response = decoder.decode(new Uint8Array(response.data));
            response = JSON.parse(response);

            if (!response.status) {
              throw new Error(response.message);
            }

            if (response.status) {
              this.generatingPvApplications[index].status =
                this.PAYMENT_VOUCHER_SUCCESS_STATUS;

              // Append all the return response zip arraybuffer to zip instance.
              zip.file(`${beneficialName}.pdf`, response.data.data, {
                compression: "DEFLATE",
                compressionOptions: {
                  level: 1,
                },
              });
            }
          })
          .catch((err) => {
            console.log(err.message);
            this.generatingPvApplications[index].status =
              this.PAYMENT_VOUCHER_FAILED_STATUS;
          });
      }

      if (Object.keys(zip.files).length == 0) {
        this.$store.dispatch("showMessage", {
          message: "Failed update, please contact administrator for support!",
          messageType: "error",
          timeout: 2000,
        });

        this.isGeneratePvDialogDisabled = false;
        return false;
      }

      // Compile all zip instance and download.
      zip
        .generateAsync({
          type: "arraybuffer",
          compression: "DEFLATE",
          compressionOptions: {
            level: 1,
          },
        })
        .then((content) => {
          const url = window.URL.createObjectURL(
            new Blob([new Uint8Array(content)])
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `YYGST HRDC ${this.name1} Refund.zip`);
          document.body.appendChild(link);
          link.click();

          // Only able to close dialog once downloaded zip file.
          this.isGeneratePvDialogDisabled = false;
        })
        .then(() => {
          // The URL is unchanged, so callApiUrl() alone would not re-request —
          // Tabulator reloaded on every setData call, BreadDataTable only
          // fetches when request state actually changes. Ask for the reload.
          this.$refs.tabulatorDataTable.clearSelection();
          this.$refs.tabulatorDataTable.refresh();
          this.getAllViews("all");
          this.$store.dispatch("showMessage", {
            message: "Updated Successfully!",
            messageType: "success",
            timeout: 2000,
          });
        })
        .catch((err) => {
          this.showErrorMessage(err);
          if (err.message) {
            this.$store.dispatch("showMessage", {
              message: err.message,
              messageType: "error",
              timeout: 2000,
            });
          }
        });
    },
    generatingPvStatusIcon(status) {
      switch (status) {
        case this.PAYMENT_VOUCHER_SUCCESS_STATUS:
          return {
            icon: "mdi-check-circle",
            color: "green",
          };
        case this.PAYMENT_VOUCHER_PENDING_STATUS:
          return {
            icon: "mdi-clock",
            color: "deep-orange lighten-2",
          };
        case this.PAYMENT_VOUCHER_FAILED_STATUS:
          return {
            icon: "mdi-close-circle",
            color: "red",
          };
        default:
          return {
            icon: "mdi-help-circle",
            color: "black",
          };
      }
    },
    previewPv(fileUrl) {
      this.showLoadingDialog();

      this.$axios
        .get(this.apiDownloadOrPreviewAttachment.url, {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            filePath: fileUrl,
          },
        })
        .then((response) => {
          const blobUrl = URL.createObjectURL(
            new Blob([response.data], { type: "application/pdf" })
          );

          window.open(blobUrl, "_blank");
        })
        .catch((error) => {
          if ((error.response.status == 404) | true) {
            this.$store.dispatch("showMessage", {
              message:
                "Failed to Preview/Download attachment, kindly contact administrator",
              messageType: "error",
              timeout: 2000,
            });
          }
          this.atchLoadingDialog = !this.atchLoadingDialog;
        })
        .finally(() => {
          this.hideLoadingDialog();
        });
    },
    toggleGeneratePvDialog(val) {
      this.generatePvDialog = val;
      if (val == false) {
        this.generatingPvApplications = null;
        this.isGeneratePvDialogDisabled = true;
      }
    },
    closeRejectDialog() {
      this.clearErrorMsg();
      this.rejectDialog = false;
    },
    toggleSelectionDialog(val) {
      this.apApplicationSelectionDialog = val;
    },
    toggleRecordsDialog(val) {
      this.recordsDialog = val;
    },
    rejectApplicationToAR(formData) {
      this.showLoadingDialog();

      this.clearErrorMsg();
      this.apiRejectApplicationToAR.setParams({
        ...formData,
        revertBackToStageId:
          this.hrdcData.stagePriority.refundEmailApToCimbRejected,
        refundEmailApToCimbRejectedStageId:
          this.hrdcData.stagePriority.refundEmailApToCimbRejected,
        userUuid: this.auth.uuid,
      });
      this.apiRejectApplicationToAR.setCallbackCompleted((response) => {
        try {
          if (!response.status) {
            this.showErrorMessage(response);
            this.hideLoadingDialog();
          }

          if (response.status) {
            this.closeRejectDialog();
            this.formData.rejectReason = null;

            this.$router.go();
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });

            this.hideLoadingDialog();
          }
        } catch (err) {
          console.log(err);
          this.alertError.push(err.message);
          return false;
        }
      });
      this.apiRejectApplicationToAR.fetch();
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .rv-page {
  background-color: var(--app-page);
}

.dark-mode .page-toolbar__title {
  color: var(--app-text);
}

.dark-mode .page-toolbar__count {
  color: var(--app-text-faint);
}

.dark-mode .rv-dialog__record-date {
  color: var(--app-text);
}

.dark-mode .rv-dialog__btn--cancel {
  background: var(--app-card);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .rv-dialog__btn--cancel:hover {
  background: var(--app-surface);
}

.dark-mode .rv-table.bordered-table {
  border: 1px solid var(--app-border);
}

.dark-mode .rv-table.bordered-table thead tr {
  background: var(--app-surface) !important;
}

.dark-mode .rv-table.bordered-table thead th {
  color: var(--app-text-muted) !important;
  border-bottom: 1px solid var(--app-border) !important;
}

.dark-mode .rv-table.bordered-table tbody td {
  color: var(--app-text) !important;
  border-bottom: 1px solid var(--app-border) !important;
}

.dark-mode .rv-table.bordered-table tbody tr:hover {
  background: var(--app-surface) !important;
}

/* >>> end auto-dark <<< */

/* Page layout */
.rv-page {
  background-color: #d8dce2;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
}

/* Page toolbar */
.page-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 16px 12px;
  flex-wrap: wrap;
  gap: 12px;
}

.page-toolbar__left {
  display: flex;
  align-items: baseline;
  gap: 12px;
  min-width: 0;
}

.page-toolbar__title {
  font-family: 'Lato', sans-serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.page-toolbar__count {
  font-family: 'Lato', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  color: #94a3b8;
  white-space: nowrap;
}

.page-toolbar__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* Search area */
.page-search {
  padding: 0 16px 16px;
}

/* Card wrapper — allows horizontal scroll on small screens */
.rv-card-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* The table keeps its full width and the CARD scrolls, rather than the columns
   compressing to fit the viewport. Matches the application browse page. */
.table-scroll-wrapper {
  min-width: 1200px;
}

/* Dialog styles */
.rv-dialog__card {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
}

.rv-dialog__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 24px;
  background: #56bde8;
}

.rv-dialog__header-icon {
  flex-shrink: 0;
}

.rv-dialog__header-title {
  font-family: 'Lato', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
}

.rv-dialog__body {
  padding: 24px;
}

.rv-dialog__record-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.rv-dialog__record-date {
  font-family: 'Lato', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
}

.rv-dialog__download-link {
  display: inline-flex;
  align-items: center;
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: #56bde8;
  text-decoration: none;
  transition: color 0.15s ease;
}

.rv-dialog__download-link:hover {
  color: #3dadd8;
}

.rv-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 24px 20px;
}

.rv-dialog__btn {
  padding: 10px 28px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
  border: none;
}

.rv-dialog__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.rv-dialog__btn--cancel {
  background: #fff;
  color: #0ea5e9;
  border: 1.5px solid #d1d5db;
}

.rv-dialog__btn--cancel:hover {
  background: #f8fafc;
  border-color: #0ea5e9;
}

.rv-dialog__btn--confirm {
  background: #56bde8;
  color: #fff;
}

.rv-dialog__btn--confirm:hover:not(:disabled) {
  background: #3dadd8;
  box-shadow: 0 4px 12px rgba(86, 189, 232, 0.3);
}

/* Table styles in dialogs */
.rv-table.bordered-table {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.rv-table.bordered-table thead tr {
  background: #f8fafc !important;
}

.rv-table.bordered-table thead th {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  color: #64748b !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 10px 12px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.rv-table.bordered-table tbody td {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.85rem !important;
  color: #334155 !important;
  padding: 10px 12px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.rv-table.bordered-table tbody tr:hover {
  background: #f8fafc !important;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 959px) {
  .rv-page {
    padding: 8px !important;
  }

  .page-toolbar {
    padding: 16px 12px 10px;
  }

  .page-toolbar__title {
    font-size: 1rem;
  }

  .page-toolbar__left {
    flex-direction: column;
    gap: 4px;
  }

  .page-search {
    padding: 0 12px 12px;
  }

  .rv-dialog__header {
    padding: 14px 18px;
  }

  .rv-dialog__header-title {
    font-size: 0.9rem;
  }

  .rv-dialog__body {
    padding: 16px;
    overflow-x: auto;
  }

  .rv-dialog__record-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .rv-dialog__actions {
    padding: 0 16px 16px;
    flex-wrap: wrap;
  }

  .rv-dialog__btn {
    padding: 8px 20px;
    font-size: 0.82rem;
    flex: 1;
    text-align: center;
    justify-content: center;
  }

  .rv-table.bordered-table thead th {
    font-size: 0.68rem !important;
    padding: 8px !important;
    white-space: nowrap;
  }

  .rv-table.bordered-table tbody td {
    font-size: 0.78rem !important;
    padding: 8px !important;
  }
}

@media (max-width: 599px) {
  .rv-page {
    padding: 8px !important;
  }

  .rv-card-wrapper {
    border-radius: 10px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  /* Toolbar */
  .page-toolbar {
    padding: 12px 10px 8px !important;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .page-toolbar__title {
    font-size: 0.95rem !important;
  }

  .page-toolbar__right {
    width: 100%;
    justify-content: flex-end;
  }

  /* Search */
  .page-search {
    padding: 0 10px 10px !important;
  }

  .rv-page >>> .tb-search-area .row {
    flex-direction: column;
  }

  .rv-page >>> .tb-search-area .col-md-6 {
    max-width: 100% !important;
    flex: 0 0 100% !important;
  }

  .rv-page >>> .tb-search-area .d-flex.justify-end {
    justify-content: flex-start !important;
    margin-top: 8px;
  }
}

/* ============================================
   DARK MODE — SCOPED
   ============================================ */
.dark-mode .rv-page { background-color: var(--app-page); }
.dark-mode .rv-card-wrapper, .dark-mode .rv-card-wrapper.v-card { background: var(--app-card) !important; }
/* The wrapper is wider than the card, so it needs its own ground or the page
   colour shows through beside the table when the card is scrolled. */
.dark-mode .table-scroll-wrapper { background: var(--app-card); }
.dark-mode .page-toolbar__title { color: var(--app-text); }
.dark-mode .page-toolbar__count { color: var(--app-text-faint); }
.dark-mode .rv-dialog__card { background: var(--app-card) !important; }
.dark-mode .rv-dialog__body { color: var(--app-text); }
.dark-mode .rv-table.bordered-table { border-color: var(--app-border); }
.dark-mode .rv-table.bordered-table thead tr { background: var(--app-page) !important; }
.dark-mode .rv-table.bordered-table thead th { color: var(--app-text-muted) !important; border-color: var(--app-border) !important; }
.dark-mode .rv-table.bordered-table tbody td { color: var(--app-text) !important; border-color: var(--app-border) !important; }
.dark-mode .rv-table.bordered-table tbody tr:hover { background: var(--app-border) !important; }
</style>

<style>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .bordered-table,
.dark-mode .bordered-table th,
.dark-mode .bordered-table td {
  border: 1px solid var(--app-border);
}

.dark-mode .rv-datepicker-card {
  background: var(--app-card);
}

.dark-mode .rv-datepicker-card__footer {
  border-top: 1px solid var(--app-border);
}

.dark-mode .rv-datepicker-card__btn--cancel {
  background: var(--app-card);
  color: var(--app-text-muted);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .rv-datepicker-card__btn--cancel:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .rv-datepicker-card__picker .v-picker__body {
  background: var(--app-card) !important;
}

.dark-mode .rv-datepicker-card__picker .v-date-picker-header {
  background: var(--app-card) !important;
}

.dark-mode .rv-datepicker-card__picker .v-date-picker-header__value button {
  color: var(--app-text) !important;
}

.dark-mode .rv-datepicker-card__picker .v-date-picker-header .v-btn {
  color: var(--app-text-muted) !important;
}

.dark-mode .rv-datepicker-card__picker .v-date-picker-header .v-btn:hover {
  background: var(--app-elevated) !important;
}

.dark-mode .rv-datepicker-card__picker .v-date-picker-table thead th {
  color: var(--app-text-faint) !important;
}

.dark-mode .rv-datepicker-card__picker .v-date-picker-table .v-btn {
  color: var(--app-text) !important;
}

.dark-mode .rv-datepicker-card__picker .v-date-picker-table .v-btn:hover {
  background: var(--app-accent-soft) !important;
}

.dark-mode .rv-datepicker-card__picker .v-date-picker-table .v-btn--disabled {
  color: var(--app-text-faint) !important;
}

/* >>> end auto-dark <<< */

/* Bordered table base */
.bordered-table,
.bordered-table th,
.bordered-table td {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

/* Dialog rounded override */
.rv-dialog {
  border-radius: 20px !important;
  overflow: hidden;
}

.rv-dialog--datepicker {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
}

/* Datepicker card wrapper */
.rv-datepicker-card {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  font-family: 'Lato', sans-serif;
}

.rv-datepicker-card__header {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
}

.rv-datepicker-card__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
}

.rv-datepicker-card__close {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.rv-datepicker-card__close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.rv-datepicker-card__footer {
  display: flex;
  gap: 10px;
  padding: 12px 20px 18px;
  border-top: 1px solid #f1f5f9;
}

.rv-datepicker-card__btn {
  flex: 1;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: center;
}

.rv-datepicker-card__btn--cancel {
  background: #fff;
  color: #64748b;
  border: 1.5px solid #d1d5db;
}

.rv-datepicker-card__btn--cancel:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.rv-datepicker-card__btn--confirm {
  background: #56bde8;
  color: #fff;
  border: none;
}

.rv-datepicker-card__btn--confirm:hover {
  background: #3dadd8;
  box-shadow: 0 4px 12px rgba(86, 189, 232, 0.3);
}

/* Picker inside card */
.rv-datepicker-card__picker {
  box-shadow: none !important;
  border-radius: 0 !important;
}

.rv-datepicker-card__picker .v-picker__body {
  background: #fff !important;
}

.rv-datepicker-card__picker .v-date-picker-header {
  padding: 12px 16px 8px !important;
  background: #fff !important;
}

.rv-datepicker-card__picker .v-date-picker-header__value button {
  font-family: 'Lato', sans-serif !important;
  font-weight: 700 !important;
  font-size: 0.95rem !important;
  color: #1e293b !important;
}

.rv-datepicker-card__picker .v-date-picker-header .v-btn {
  color: #64748b !important;
}

.rv-datepicker-card__picker .v-date-picker-header .v-btn:hover {
  background: #f1f5f9 !important;
}

.rv-datepicker-card__picker .v-date-picker-table thead th {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  color: #94a3b8 !important;
  padding: 8px 0 !important;
}

.rv-datepicker-card__picker .v-date-picker-table .v-btn {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.82rem !important;
  font-weight: 500 !important;
  color: #334155 !important;
}

.rv-datepicker-card__picker .v-date-picker-table .v-btn:hover {
  background: #f0f9ff !important;
}

.rv-datepicker-card__picker .v-date-picker-table .v-btn--active {
  background: #0ea5e9 !important;
  color: #fff !important;
  font-weight: 700 !important;
}

.rv-datepicker-card__picker .v-date-picker-table .v-btn--active:hover {
  background: #0284c7 !important;
}

.rv-datepicker-card__picker .v-date-picker-table .v-btn--outlined {
  border-color: #0ea5e9 !important;
  color: #0ea5e9 !important;
}

.rv-datepicker-card__picker .v-date-picker-table .v-btn--disabled {
  color: #cbd5e1 !important;
}

.rv-datepicker-card__picker .v-date-picker-years li,
.rv-datepicker-card__picker .v-date-picker-months .v-btn {
  font-family: 'Lato', sans-serif !important;
}

/* Tabulator action buttons */
.tb-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-family: 'Lato', sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
  white-space: nowrap;
  margin: 1px;
  vertical-align: middle;
}

.tb-action-btn--blue {
  background: #e0f2fe;
  color: #0369a1;
}

.tb-action-btn--blue:hover {
  background: #bae6fd;
  color: #0c4a6e;
}

.tb-action-btn--red {
  background: #fee2e2;
  color: #dc2626;
}

.tb-action-btn--red:hover {
  background: #fecaca;
  color: #b91c1c;
}

/* Row action buttons — "View PDF" / "Reject".
   Reject was the app's one solid-fill status button: --app-danger (#f87171) as
   the FILL with --app-danger-fg (#fecaca) as the text, i.e. pale pink on mid
   red at 1.91:1 — the pair is designed as wash + text, never as fill + text.
   Both buttons now follow the documented model (translucent wash, bright text,
   1px ring), which also makes them read as a set. The ring is an inset shadow
   rather than a border so dark mode adds no layout width — the Action column is
   a fixed 190px and both buttons have to stay on one line.
   Contrast on the dark row (--tbl-surface): Reject 7.18:1, View PDF 6.00:1. */
.dark-mode .tb-action-btn--blue {
  background: var(--app-accent-soft);
  color: var(--app-accent-strong);
  box-shadow: inset 0 0 0 1px var(--app-accent-border);
}
.dark-mode .tb-action-btn--blue:hover {
  background: var(--app-info-bg);
  color: var(--app-info-fg);
  box-shadow: inset 0 0 0 1px var(--app-accent);
}
.dark-mode .tb-action-btn--red {
  background: var(--app-danger-bg);
  color: var(--app-danger-fg);
  box-shadow: inset 0 0 0 1px var(--app-danger-border);
}
.dark-mode .tb-action-btn--red:hover {
  background: var(--app-danger-bg-strong);
  color: var(--app-danger-fg);
  box-shadow: inset 0 0 0 1px var(--app-danger);
}
.dark-mode .rv-datepicker-card { background: var(--app-card); }
.dark-mode .rv-datepicker-card__picker .v-picker__body { background: var(--app-card) !important; }
.dark-mode .rv-datepicker-card__footer { border-color: var(--app-border); }
</style>
